// PlanDescription.jsx

import React, { useState } from "react";
import { useParams } from "react-router-dom";

import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { blue } from "@mui/material/colors";
import DistributionFormModal from "../../../components/DistributionFormModal/DistributionFormModal";

/**
 * If you're using <ListItemLink> in other files, you can remove or adapt:
 */
// const ListItemLink = (props) => <ListItem button component="a" {...props} />;

function PlanDescription({
  // Props you'll likely pass from parent:
  employerState = "",
  selectedPlanType = {},
  selectedPlanUniversalPlanValue = "",
  planDescriptionDocuments = [],
  approvedVendors = [],
  showPayroll = false,
  payrollTitle = "Payroll Schedule",
  // Optionally pass "showSPD" or derive from selectedPlanType.showSPD:
  showSPD = selectedPlanType?.showSPD ?? true,
  parentWebsite = null,
  track = null,
}) {
  /**
   * We can derive employerId (if needed) via useParams or from parent props.
   * Adjust as needed based on how you're structuring your routes.
   */
  const { employerId } = useParams();

  const [distributionModal, setDistributionModal] = useState({
    open: false,
    planType: '',
    docURL: ''
  });

  // Construct full parent URL for links - works for all 5 WordPress sites
  const getParentUrl = (url) => {
    // Check if running inside an iframe
    if (window.parent && window.parent !== window) {
      // Use parentWebsite from postMessage (received from WordPress parent)
      // Falls back to tcgservices.com if not provided
      const parentDomain = parentWebsite || 'tcgservices.com';

      // Determine base path based on environment (TEST vs PROD)
      const basePath = window.location.hostname.includes('test')
        ? '/documents-test/'
        : '/documents/';

      return `https://${parentDomain}${basePath}#${url}`;
    }
    // If not in iframe (standalone mode), return the relative URL
    return url;
  };

  // Handle opening links in new tabs.
  // `meta` is the tracking payload; the beacon fires here at the source rather
  // than on the destination page, which also calls getEmployerDetails and would
  // otherwise double-count the same interaction.
  const handleNewTabClick = (e, url, meta) => {
    e.preventDefault();
    if (track && meta) track(meta);
    const targetUrl = getParentUrl(url);
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  // For example, if you want to link to SPD at:
  // /search-forms/:employerId/:planId/spd
  // We'll build a link from scratch using employerId & plan info:
  const spdUrl = `/search-forms/${employerId}/${selectedPlanType.code}-${selectedPlanUniversalPlanValue}/spd`;

  // Similarly, link to payroll:
  // /search-forms/:employerId/:planId/payroll
  // We’ll build a link from scratch using employerId & plan info:
  const payrollUrl = `/search-forms/${employerId}/${selectedPlanType.code}-${selectedPlanUniversalPlanValue}/payroll`;

  // Check if we should show an Investment Providers link
  // (Remove/adjust if your logic differs.)
  const shouldShowApprovedVendorsLink = (vendors, planType) => {
    return vendors.some((v) =>
      v?.planType?.replace(/\s/g, "") === planType.replace(/\s/g, "")
    );
  };

  // Decide if we should display the big "Plan Description" heading
  // We only show it if we have some content—like SPD, docs, payroll, or vendors link
  const hasAnyPlanDescriptionContent =
    showSPD ||
    showPayroll ||
    planDescriptionDocuments.length > 0 ||
    shouldShowApprovedVendorsLink(approvedVendors, selectedPlanType.code);

  const handleDocClick = (e, doc) => {
    e.preventDefault();

    if (track) {
      track({
        event_type: "download",
        item_type: "document",
        item_id: doc.documentId,
        item_name: doc.documentName || doc.docName,
        item_section: doc.displaySection || "Plan Description",
        plan_type: selectedPlanType?.code,
      });
    }

    // Modal disabled - admin distribution forms now download directly
    // const docName = doc.documentName || doc.docName || '';
    // if (docName.toLowerCase().includes('admin distribution')) {
    //   // Show modal for distribution forms
    //   setDistributionModal({
    //     open: true,
    //     planType: selectedPlanType.code,
    //     docURL: doc.docURL
    //   });
    //   return;
    // }

    // For all forms, proceed with immediate download
    // Check if we're in an iframe environment
    if (window.triggerExternalVendorLink && window.parent !== window) {
      // In iframe: use the external navigation system
      window.triggerExternalVendorLink(doc.docURL);
    } else {
      // Standalone or fallback: create a temporary anchor and click it
      // This approach is more reliable with popup blockers
      const link = document.createElement('a');
      link.href = doc.docURL;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDistributionCancel = () => {
    setDistributionModal({ open: false, planType: '', docURL: '' });
  };

  const handleDistributionConfirm = () => {
    const docURL = distributionModal.docURL;

    // Same download logic as non-distribution forms
    if (window.triggerExternalVendorLink && window.parent !== window) {
      window.triggerExternalVendorLink(docURL);
    } else {
      const link = document.createElement('a');
      link.href = docURL;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    // Close modal
    setDistributionModal({ open: false, planType: '', docURL: '' });
  };

  return (
    <>
      {hasAnyPlanDescriptionContent && (
        <Typography
          variant="h6"
          sx={{
            margin: "8px 0",
            padding: "0 8px",
            backgroundColor: blue[700],
            color: "white",
          }}
        >
          Plan Description
        </Typography>
      )}

      <List sx={{ mb: 2 }}>
        {/* SPD Link */}
        {showSPD && (
          <ListItem
            button
            component="a"
            href={getParentUrl(spdUrl)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) =>
              handleNewTabClick(e, spdUrl, {
                event_type: "view",
                item_type: "spd",
                item_id: `${selectedPlanType.code}-${selectedPlanUniversalPlanValue}`,
                item_name: `${selectedPlanType.label} Summary Plan Description`,
                plan_type: selectedPlanType.code,
              })
            }
          >
            <ListItemText
              primary={`${selectedPlanType.label} Summary Plan Description`}
              sx={{ color: blue[700] }}
            />
          </ListItem>
        )}

        {/* Payroll Dates link */}
        {showPayroll && (
          <ListItem
            button
            component="a"
            href={getParentUrl(payrollUrl)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) =>
              handleNewTabClick(e, payrollUrl, {
                event_type: "view",
                item_type: "payroll",
                item_id: `${selectedPlanType.code}-${selectedPlanUniversalPlanValue}`,
                item_name: payrollTitle,
                item_section: "Payroll Dates",
                plan_type: selectedPlanType.code,
              })
            }
          >
            <ListItemText
              primary={payrollTitle}
              sx={{ color: blue[700] }}
            />
          </ListItem>
        )}

        {/* Investment Providers (optional) */}
        {(() => {
          // Check if there are vendors for this plan type
          const hasVendors = approvedVendors && approvedVendors.some(vendor => {
            // Normalize both plan types for comparison
            const normalizedVendorPlanType = vendor.planType.replace(/\s/g, "").replace(/[()]/g, "").toLowerCase();
            const normalizedPlanType = selectedPlanType.code.replace(/\s/g, "").replace(/[()]/g, "").toLowerCase();
            return normalizedVendorPlanType === normalizedPlanType;
          });

          // Only show the link if there are vendors OR if it's a non-admin plan that should show vendors
          const shouldShow = hasVendors || shouldShowApprovedVendorsLink(approvedVendors, selectedPlanType.code);

          const vendorUrl = `/search-forms/${employerId}/${employerState}/approved-vendor/${encodeURIComponent(selectedPlanType.code)}`;

          return shouldShow ? (
            <ListItem
              button
              component="a"
              href={getParentUrl(vendorUrl)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) =>
                handleNewTabClick(e, vendorUrl, {
                  event_type: "view",
                  item_type: "vendor",
                  item_id: `${selectedPlanType.code}-vendors`,
                  item_name: "Investment Provider List",
                  plan_type: selectedPlanType.code,
                })
              }
            >
              <ListItemText
                primary={`${selectedPlanType.label} Investment Provider List`}
                sx={{ color: blue[700] }}
              />
            </ListItem>
          ) : null;
        })()}

        {/* Additional "Plan Description" documents (if any) */}
        {planDescriptionDocuments.map((doc) => (
          <ListItem
            key={doc.documentId || doc.doc_uuid}
            button
            onClick={(e) => handleDocClick(e, doc)}
            sx={{ cursor: 'pointer' }}
          >
            <ListItemText
              primary={doc.documentName || doc.docName}
              sx={{ color: blue[700] }}
            />
          </ListItem>
        ))}
      </List>
      <DistributionFormModal
        open={distributionModal.open}
        planType={distributionModal.planType}
        onCancel={handleDistributionCancel}
        onConfirm={handleDistributionConfirm}
      />
    </>
  );
}

export default PlanDescription;
